import {useEffect, useRef} from 'react';
import {UserService} from '../services/openapi';
import {useSessionContext} from 'supertokens-auth-react/recipe/session';

const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const LAST_SESSION_CHECK_KEY = 'lastSessionCheck';
const LAST_HARD_REFRESH_KEY = 'lastHardRefresh';
const HARD_REFRESH_INTERVAL = 55 * 60 * 1000; // 55 minutes (before session expires at 60)
const SESSION_LOAD_FLAG = 'sessionLoadFlag'; // Used to detect fresh page loads

/**
 * Custom hook that periodically validates the SuperTokens session by making
 * a lightweight API call to the backend. If the session is invalid or expired,
 * SuperTokens will automatically attempt to refresh it. If refresh fails
 * (session completely expired), the page will be reloaded to force re-authentication.
 *
 * Additionally, this hook enforces a hard refresh every 55 minutes to ensure
 * the application stays up-to-date. The 55-minute interval is chosen to refresh
 * BEFORE the typical 60-minute session expiration, avoiding issues with expired tokens.
 * The timer is automatically reset when the user manually reloads the page.
 *
 * This approach solves the issue where user navigation counts as "activity"
 * but doesn't actually refresh the session, leading to silent session expiration.
 */
export const useInactivityRefresh = () => {
    const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isCheckingRef = useRef<boolean>(false);
    const sessionContext = useSessionContext();
    const hasSession = !sessionContext.loading && sessionContext.doesSessionExist;

    useEffect(() => {
        if (!hasSession) {
            return;
        }

        // Detect if this is a fresh page load (hard reload, browser refresh, or new tab)
        // sessionStorage is cleared on page reload, so if the flag is missing, it's a fresh load
        const isNewPageLoad = !sessionStorage.getItem(SESSION_LOAD_FLAG);

        if (isNewPageLoad) {
            console.log('[Hard Refresh] Detected fresh page load, resetting hard refresh timestamp');
            localStorage.setItem(LAST_HARD_REFRESH_KEY, Date.now().toString());
            sessionStorage.setItem(SESSION_LOAD_FLAG, 'true');
        }

        // Check if session is still valid by making a lightweight API call
        const checkSessionValidity = async () => {
            // Prevent concurrent checks
            if (isCheckingRef.current) {
                return;
            }

            isCheckingRef.current = true;
            const now = Date.now();

            // Check if hard refresh is needed (60 minutes elapsed)
            const lastHardRefreshStr = localStorage.getItem(LAST_HARD_REFRESH_KEY);
            if (lastHardRefreshStr) {
                const lastHardRefresh = parseInt(lastHardRefreshStr, 10);
                const timeSinceLastHardRefresh = now - lastHardRefresh;
                const minutesElapsed = Math.floor(timeSinceLastHardRefresh / 1000 / 60);

                if (timeSinceLastHardRefresh >= HARD_REFRESH_INTERVAL) {
                    console.log('[Hard Refresh] 55 minutes elapsed since last refresh, refreshing session before reload...');

                    try {
                        // Make an API call to ensure session is valid and refreshed before reloading
                        await UserService.readUsersMeUserMeGet();
                        console.log('[Hard Refresh] Session validated, proceeding with hard refresh');

                        // Small delay to ensure session refresh is complete
                        await new Promise(resolve => setTimeout(resolve, 500));

                        localStorage.setItem(LAST_HARD_REFRESH_KEY, now.toString());
                        window.location.reload();
                        return; // Exit early since we're refreshing the page
                    } catch (error: any) {
                        console.error('[Hard Refresh] Session validation failed before hard refresh:', error);

                        // If session is invalid (401), try to let SuperTokens refresh it
                        if (error?.status === 401) {
                            console.log('[Hard Refresh] Session expired, attempting SuperTokens refresh...');

                            // Wait for SuperTokens to attempt refresh
                            await new Promise(resolve => setTimeout(resolve, 1000));

                            try {
                                // Try again after SuperTokens refresh
                                await UserService.readUsersMeUserMeGet();
                                console.log('[Hard Refresh] Session refreshed successfully, proceeding with hard refresh');

                                localStorage.setItem(LAST_HARD_REFRESH_KEY, now.toString());
                                window.location.reload();
                                return;
                            } catch (retryError) {
                                console.error('[Hard Refresh] Session still invalid after refresh attempt, reloading anyway...');
                                // Reload anyway - the login page will handle re-authentication
                                localStorage.setItem(LAST_HARD_REFRESH_KEY, now.toString());
                                window.location.reload();
                                return;
                            }
                        } else {
                            // For network errors or other issues, reload anyway
                            console.log('[Hard Refresh] Non-auth error, proceeding with hard refresh anyway');
                            localStorage.setItem(LAST_HARD_REFRESH_KEY, now.toString());
                            window.location.reload();
                            return;
                        }
                    }
                } else {
                    console.log(`[Hard Refresh] ${minutesElapsed} minutes elapsed, ${55 - minutesElapsed} minutes until hard refresh`);
                }
            } else {
                // Initialize if not set (shouldn't happen due to page load detection, but safety check)
                console.log('[Hard Refresh] Initializing hard refresh timestamp');
                localStorage.setItem(LAST_HARD_REFRESH_KEY, now.toString());
            }

            try {
                console.log('[Session Check] Validating session at:', new Date().toISOString());

                // Make a lightweight API call to verify session
                const res = await UserService.readUsersMeUserMeGet();
                console.log('res')
                console.log(res)

                // Session is valid, update last check timestamp
                localStorage.setItem(LAST_SESSION_CHECK_KEY, now.toString());
                console.log('[Session Check] Session valid');

            } catch (error: any) {
                console.error('[Session Check] Session validation failed:', error);

                // Check if it's a 401 (Unauthorized) error
                if (error?.status === 401) {
                    console.log('[Session Check] Session expired (401). SuperTokens should attempt refresh...');

                    // Give SuperTokens a moment to attempt auto-refresh, then check again
                    setTimeout(async () => {
                        try {
                            await UserService.readUsersMeUserMeGet();
                            console.log('[Session Check] Session successfully refreshed by SuperTokens');
                            localStorage.setItem(LAST_SESSION_CHECK_KEY, Date.now().toString());
                        } catch (retryError: any) {
                            // If still failing after refresh attempt, session is completely dead
                            console.error('[Session Check] Session refresh failed. Reloading page...');
                            window.location.reload();
                        }
                    }, 1000);
                } else {
                    // For other errors (network issues, etc.), just log and continue
                    console.log('[Session Check] Non-auth error, will retry on next interval');
                }
            } finally {
                isCheckingRef.current = false;
            }
        };

        // Initialize last check timestamp
        const lastCheckStr = localStorage.getItem(LAST_SESSION_CHECK_KEY);
        if (!lastCheckStr) {
            localStorage.setItem(LAST_SESSION_CHECK_KEY, Date.now().toString());
        }

        // Perform initial check after a short delay
        const initialCheckTimeout = setTimeout(() => {
            checkSessionValidity();
        }, 5000); // Check 5 seconds after mount

        // Start periodic session validation
        checkIntervalRef.current = setInterval(checkSessionValidity, SESSION_CHECK_INTERVAL);

        console.log('[Session Check] Hook initialized. Will check session every', SESSION_CHECK_INTERVAL / 1000 / 60, 'minutes');

        // Cleanup
        return () => {
            clearTimeout(initialCheckTimeout);
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
            }
        };
    }, [hasSession]);
};
